import * as React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';


import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { DatePicker } from '@mui/lab';
import { format } from 'date-fns';
import request from '../../utils/request';

export default function UploadEndorsementDoc({
  editData,
  closeModal,
  setShowSnackBarAlert,
}) {
  const [loader, setLoader] = React.useState(false);

  const SUPPORTED_FORMATS = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'application/pdf',
  ];

  const validationSchema = Yup.object().shape({
    endorsementDocuement: Yup.mixed()
      .required('Endorsement Document is required')
      .test(
        'fileSize',
        'File is too large (Max size: 5 MB)',
        (value) => !value || (value && value.size <= 5 * 1024 * 1024)
      )
      .test(
        'fileFormat',
        'Only image and PDF files are allowed',
        (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))
      ),
    endorsementNumber: Yup.string().required('Endorsement Number is required'),
    endorsementReceivedDate: Yup.string().required('Endorsement Received Date is required'),
    endorsementEffectiveDate: Yup.string().required('Endorsement Effective Date is required'),
    endorsementDocumentIssueDate: Yup.string().required('Endorsement Document Issue Date is required'),
  });

  const formik = useFormik({
    initialValues: {
      endorsementDocuement: '',
      endorsementNumber: '',
      endorsementReceivedDate: '',
      endorsementEffectiveDate: '',
      endorsementDocumentIssueDate: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoader(true);

      const formData = new FormData();
      formData.append('endorsementDocuement', values.endorsementDocuement);
      formData.append('endorsementId', editData.id);
      formData.append('endorsementNumber', values.endorsementNumber);
      formData.append('endorsementReceivedDate', values.endorsementReceivedDate);
      formData.append('endorsementEffectiveDate', values.endorsementEffectiveDate);
      formData.append('endorsementDocumentIssueDate', values.endorsementDocumentIssueDate);

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      try {
        const response = await request.post(
          'endorsement/uploadEndorsementDocument',
          formData,
          config
        );
        setLoader(false);

        if (response.status) {
          setShowSnackBarAlert({
            status: true,
            type: 'success',
            message: 'Endorsement document uploaded successfully',
          });
          closeModal();
        } else {
          setShowSnackBarAlert({
            status: true,
            type: 'error',
            message: response['message'] || 'Upload failed',
          });
        }
      } catch (error) {
        console.error(error);
        setLoader(false);
        setShowSnackBarAlert({
          status: true,
          type: 'error',
          message: error['message'] || 'Something went wrong while uploading',
        });
      }
    },
  });

  const handleFileChange = (event) => {
    formik.setFieldValue('endorsementDocuement', event.currentTarget.files[0]);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={true}
      onClose={() => closeModal(false)}
    >
      <DialogTitle>Upload Endorsement Document</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  name="endorsementNumber"
                  label={
                    <React.Fragment>
                      Endorsement Number{' '}
                      <Typography component="span" sx={{ color: 'red' }}>
                        *
                      </Typography>
                    </React.Fragment>
                  }

                  value={formik.values.endorsementNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.endorsementNumber && Boolean(formik.errors.endorsementNumber)}
                  helperText={formik.touched.endorsementNumber && formik.errors.endorsementNumber}
                />
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  views={['year', 'month', 'day']}
                  label={
                    <React.Fragment>
                      Endorsement Received Date{' '}
                      <Typography component="span" sx={{ color: 'red' }}>
                        *
                      </Typography>
                    </React.Fragment>
                  }
                  inputFormat="dd-MM-yyyy"
                  value={formik.values.endorsementReceivedDate || null}
                  onChange={(newValue) => {
                    formik.setFieldValue(
                      'endorsementReceivedDate',
                      newValue ? format(new Date(newValue), 'yyyy-MM-dd') : ''
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      onKeyDown={(e) => e.preventDefault()}
                      error={formik.touched.endorsementReceivedDate && Boolean(formik.errors.endorsementReceivedDate)}
                      helperText={formik.touched.endorsementReceivedDate && formik.errors.endorsementReceivedDate}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  views={['year', 'month', 'day']}
                  label={
                    <React.Fragment>
                      Endorsement Effective Date{' '}
                      <Typography component="span" sx={{ color: 'red' }}>
                        *
                      </Typography>
                    </React.Fragment>
                  }

                  inputFormat="dd-MM-yyyy"
                  value={formik.values.endorsementEffectiveDate || null}
                  onChange={(newValue) => {
                    formik.setFieldValue(
                      'endorsementEffectiveDate',
                      newValue ? format(new Date(newValue), 'yyyy-MM-dd') : ''
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      onKeyDown={(e) => e.preventDefault()}
                      error={formik.touched.endorsementEffectiveDate && Boolean(formik.errors.endorsementEffectiveDate)}
                      helperText={formik.touched.endorsementEffectiveDate && formik.errors.endorsementEffectiveDate}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  views={['year', 'month', 'day']}
                  label={
                    <React.Fragment>
                      Endorsement Document Issue Date{' '}
                      <Typography component="span" sx={{ color: 'red' }}>
                        *
                      </Typography>
                    </React.Fragment>
                  }

                  inputFormat="dd-MM-yyyy"
                  value={formik.values.endorsementDocumentIssueDate || null}
                  onChange={(newValue) => {
                    formik.setFieldValue(
                      'endorsementDocumentIssueDate',
                      newValue ? format(new Date(newValue), 'yyyy-MM-dd') : ''
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      onKeyDown={(e) => e.preventDefault()}
                      error={formik.touched.endorsementDocumentIssueDate && Boolean(formik.errors.endorsementDocumentIssueDate)}
                      helperText={formik.touched.endorsementDocumentIssueDate && formik.errors.endorsementDocumentIssueDate}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="file"
                  label={
                    <React.Fragment>
                      Endorsement Document{' '}
                      <Typography component="span" sx={{ color: 'red' }}>
                        *
                      </Typography>
                    </React.Fragment>
                  }

                  InputLabelProps={{ shrink: true }}
                  onChange={handleFileChange}
                  error={formik.touched.endorsementDocuement && Boolean(formik.errors.endorsementDocuement)}
                  helperText={formik.touched.endorsementDocuement && formik.errors.endorsementDocuement}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>

          <LoadingButton
            loading={loader}
            variant="contained"
            type="submit"
          >
            Upload
          </LoadingButton>
          <Button onClick={() => closeModal(false)} color="error" variant="contained">
            Cancel
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
